import {gql} from 'graphql-request';
import {useGqlRequest} from '@/api/request';
import {
  QueryFunctionContext,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import {
  AnalysisItem,
  KodataNearbyStoreCountByCategory,
  KodataNearbyStoreItem,
  KodataStoreData,
  KodataStoreDetailData,
  KodataStoreQueryFilter,
} from '../@types';
import {getAreaQueryVariables} from './analytics';

interface Variables {
  filter?: KodataStoreQueryFilter;
  page?: number;
  perPage?: number;
  sort?: string;
}

interface KodataStoreResult {
  result?: {
    count: number;
    items: KodataStoreData[];
    pageInfo: {
      currentPage: number;
      hasNextPage: boolean;
    };
  };
}

export const useKodataStoreInfiniteQuery = (variables?: Variables) => {
  const request = useGqlRequest({
    query: KODATA_STORE_PAGINATION,
  });
  const sort = variables?.sort || 'EST3AVERAGESALES_DESC';
  const filter = variables?.filter || {};
  const perPage = variables?.perPage || 30;

  const enabled = !!Object.values(filter).length;

  const queryFn = ({
    pageParam = variables,
  }: QueryFunctionContext): undefined | Promise<KodataStoreResult> => {
    if (!pageParam) {
      return;
    }

    return request({
      ...pageParam,
      ...filter,
      sort,
      perPage,
    });
  };

  return useInfiniteQuery({
    enabled,
    queryKey: ['kodata', 'stores-list', filter, sort],
    initialPageParam: {
      ...filter,
      sort,
      perPage,
      page: 1,
    },
    queryFn,
    getNextPageParam: (lastPage, pages) => {
      if (!variables || !pages || !lastPage?.result?.pageInfo?.hasNextPage) {
        return null;
      }

      return {
        ...filter,
        sort,
        perPage,
        page: lastPage?.result?.pageInfo.currentPage + 1,
      };
    },
  });
};

export const useKodataStoreById = (id: string) => {
  const request = useGqlRequest({
    query: KODATA_STORE_BY_ID,
  });

  const queryFn = (
    _: QueryFunctionContext
  ):
    | undefined
    | Promise<{
        result?: KodataStoreDetailData;
      }> => {
    if (!id) {
      return;
    }

    return request({_id: id});
  };

  const select = (data?: {result?: KodataStoreDetailData}) => data?.result;

  return useQuery({
    enabled: !!id,
    queryKey: ['kodata', 'store', id],
    queryFn,
    select,
  });
};

export const useKodataNearbyStoreCountByPoint = (item: AnalysisItem) => {
  const request = useGqlRequest({
    query: KODATA_AROUND_STORE_COUNT_BY_CATEGORY,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: KodataNearbyStoreCountByCategory[];
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant?.radius) {
      return;
    }

    const {radius, ...vars} = variant;

    return request({...vars, distance: radius});
  };

  const id = item?.id;

  const select = (data?: {result?: KodataNearbyStoreCountByCategory[]}) =>
    data?.result;

  return useQuery({
    queryKey: ['nearby', 'kodata-store-count', id],
    enabled: !!id,
    queryFn,
    select,
  });
};

export const useKodataNearbyStoresByPoint = (
  item: AnalysisItem,
  categories?: string[],
  limit?: number
) => {
  const request = useGqlRequest({
    query: KODATA_NEARBY_STORE_BY_POINT,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: KodataNearbyStoreItem[];
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant?.radius) {
      return;
    }

    const {radius, ...vars} = variant;

    return request({...vars, distance: radius, categories, limit});
  };

  const id = item?.id;

  const select = (data?: {result?: KodataNearbyStoreItem[]}) => {
    return data?.result;
  };

  return useQuery({
    queryKey: ['nearby', 'kodata-stores-by-point', id, categories],
    enabled: !!id && !!categories,
    queryFn,
    select,
  });
};

const KODATA_STORE_PAGINATION = gql`
  query kodataStorePagination(
    $sido: String!
    $sgg: String!
    $emd: String!
    $categories: [String!]
    $salesRanges: [SalesRange!]
    $nameRegex: String
    $page: Float!
    $perPage: Float!
    $sort: String!
  ) {
    result: kodataStorePagination(
      sido: $sido
      sgg: $sgg
      emd: $emd
      categories: $categories
      salesRanges: $salesRanges
      nameRegex: $nameRegex
      page: $page
      perPage: $perPage
      sort: $sort
    ) {
      count
      items {
        id
        brandName
        businessItemName
        est3AverageSales
        location {
          coordinates
        }
      }
      pageInfo {
        currentPage
        hasNextPage
      }
    }
  }
`;

const KODATA_STORE_BY_ID = gql`
  query kodataStoreById($_id: String!) {
    result: kodataStoreById(_id: $_id) {
      id
      brandName
      businessItemName
      address
      sales
      est3AverageSales
      baseDate
      location {
        coordinates
      }
    }
  }
`;

const KODATA_AROUND_STORE_COUNT_BY_CATEGORY = gql`
  query kodataAroundInfoGroupCategory(
    $distance: Float!
    $lat: Float!
    $lng: Float!
  ) {
    result: kodataAroundInfoGroupCategory(
      distance: $distance
      lat: $lat
      lng: $lng
    ) {
      category
      count
    }
  }
`;

const KODATA_NEARBY_STORE_BY_POINT = gql`
  query kodataAroundInfoPonitBy(
    $categories: [String!]!
    $distance: Float!
    $lat: Float!
    $lng: Float!
    $limit: Float
  ) {
    result: kodataAroundInfoPonitBy(
      categories: $categories
      distance: $distance
      lat: $lat
      lng: $lng
      limit: $limit
    ) {
      address
      baseDate
      branchName
      category
      categoryDetail
      newStore
      location {
        coordinates
        type
      }
      no
    }
  }
`;
